import React from 'react';
import TeachEnglishPageLayout from '../components/TeachEnglishPageLayout';

const TeachingWriting = () => {
  const title = 'Teaching Writing';
  const image = '/Images/writing1-1280x470.webp';
  const intro = 'Writing makes thinking visible. This guide walks you through genre-based instruction, process writing stages, feedback that moves writers forward, and scaffolding for every CEFR level.';

  const seoContent = [
    'Teach writing by genre, not just \"write a paragraph.\" Each genre (email, essay, report, review, narrative) has a predictable structure, language features, and audience expectations. A genre-based cycle: 1) Model analysis (deconstruct a strong example), 2) Joint construction (write together as a class), 3) Independent construction (learners write their own), 4) Comparison & reflection.',
    'Process writing mirrors real composing: Plan → Draft → Revise → Edit → Publish. Don\'t skip stages. Planning: brainstorming, outlining, graphic organizers. Drafting: fluency focus, ignore errors. Revising: macro changes (structure, coherence, audience). Editing: micro (grammar, vocab, mechanics). Publishing: share with real audience (blog, pen pal, class magazine).',
    'Feedback that works: focus on 2–3 priorities per draft, not everything. Use a coding system (WC = word choice, VF = verb form, SS = sentence structure) so learners self-correct. Balance product feedback (\"Your thesis is clear\") with process feedback (\"Try outlining before drafting next time\"). Peer feedback trains the editor\'s eye—give checklists, not red pens.',
    'Scaffold by level. A1: sentence frames, picture prompts, 3-sentence texts. A2: paragraph templates, linking words bank, guided emails. B1: essay skeleton (intro/body/conclusion), cohesion device menu, peer checklist. B2: genre analysis, counter-argument integration, source synthesis. C1: register shifting, abstract nouns, hedging/boosting, citation.',
    'Integrate writing with other skills. Read-to-write: summarize, respond, synthesize sources. Listen-to-write: lecture notes → summary. Speak-to-write: discussion → position paper. The best writing tasks emerge from genuine communicative needs, not isolated prompts.',
  ];

  const levels = [
    {
      level: 'genres',
      label: 'Genre Packs',
      tagline: 'Model + template + checklist',
      cards: [
        {
          title: 'Email & Message Pack',
          image: '/Images/grammar1.webp',
          desc: 'Formal/informal emails, WhatsApp-style texts, request/complaint/apology with phrase banks.',
          links: [{ text: 'Download', to: '/writing-exercises/emails' }],
        },
        {
          title: 'Essay & Report Pack',
          image: '/Images/2people1.webp',
          desc: 'Opinion essay, for/against, problem-solution, business report—skeletons + model texts.',
          links: [{ text: 'Download', to: '/writing-exercises/essays-reports' }],
        },
        {
          title: 'Creative & Narrative Pack',
          image: '/Images/vocabulary.webp',
          desc: 'Story mountain, character profile, dialogue punctuation, show-don\'t-tell exercises.',
          links: [{ text: 'Download', to: '/writing-exercises/creative' }],
        },
      ],
    },
    {
      level: 'process',
      label: 'Process Writing Tools',
      tagline: 'Support every stage',
      cards: [
        {
          title: 'Graphic Organizer Library',
          image: '/Images/online-teacher.webp',
          desc: 'Mind maps, fishbone, Venn, hamburger paragraph, essay outline—editable PDFs.',
          links: [{ text: 'Download', to: '/resources/graphic-organizers.pdf' }],
        },
        {
          title: 'Feedback Coding Key',
          image: '/Images/tutor2-1280x470.webp',
          desc: 'Standardized symbol set (WC, VF, WO, P, SP, ^) + student self-correction worksheet.',
          links: [{ text: 'Get Key', to: '/resources/feedback-coding.pdf' }],
        },
        {
          title: 'Peer Review Checklists',
          image: '/Images/3people1a.webp',
          desc: 'Genre-specific: email, essay, story—focus on task, organization, language, mechanics.',
          links: [{ text: 'Download', to: '/resources/peer-checklists.pdf' }],
        },
      ],
    },
  ];

  return (
    <TeachEnglishPageLayout
      title={title}
      image={image}
      intro={intro}
      seoContent={seoContent}
      levels={levels}
      seoTitle="Teaching Writing: Genre-Based Approach, Process Stages & Feedback Strategies"
      seoDescription="Complete ESL writing guide. Genre analysis cycle (model → joint → independent), process writing stages, targeted feedback coding, CEFR-level scaffolding, and free downloadable genre packs with models and checklists."
    />
  );
};

export default TeachingWriting;